import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePredictionStore } from 'stores/predictionStore';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PredictionsTable from '../components/PredictionsTable';
import { TableRow, ColumnConfig, ExportFormat } from '../types';
import { routes } from 'routes/routes';
import { LOAD_TABLE_FIELDS } from '../constants/table_fields';
import { useHasPermissions, useIsSuperbUser } from 'hooks/useUser';
import { PERMISSIONS } from 'types/user';
import { asPrice } from 'utils/money';
import { formatScore } from '../utils/formatScore';

const DetailedRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { predictionResult, isLoading, error } = usePredictionStore();
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const isSuperbUser = useIsSuperbUser();
  const hasCanSeeDetailedPredictions = useHasPermissions([PERMISSIONS.CAN_SEE_DETAILED_PREDICTIONS]);

  useEffect(() => {
    if (!(isSuperbUser && hasCanSeeDetailedPredictions)) {
      history.replace(routes.PREDICTIONS_ENGINE);
    }
  }, [isSuperbUser, hasCanSeeDetailedPredictions, history]);

  const generateColumns = (): ColumnConfig[] => {
    const baseColumns: ColumnConfig[] = [
      {
        key: LOAD_TABLE_FIELDS.internal_id, 
        header: "Internal ID", 
        cell: { type: 'readonly' },
        width: 370,
        resizable: true
      },
      {
        key: LOAD_TABLE_FIELDS.version_id,
        header: "Version ID", 
        cell: { type: 'readonly' },
        width: 120
      },
      {
        key: LOAD_TABLE_FIELDS.title, 
        header: "Title", 
        cell: { type: 'readonly' },
        width: 260,
        resizable: true
      },
      {
        key: LOAD_TABLE_FIELDS.release_date, 
        header: "Release Date", 
        cell: { type: 'readonly' },
        width: 185
      },
      {
        key: LOAD_TABLE_FIELDS.box_office_multiple, 
        header: "Box Office Multiple", 
        cell: { type: 'readonly' },
        width: 100
      },
      {
        key: LOAD_TABLE_FIELDS.resulting_gross, 
        header: "Resulting Gross", 
        cell: { type: 'readonly' },
        width: 140,
        formatter: (value) => `$${asPrice(value)}`,
      },
      {
        key: LOAD_TABLE_FIELDS.score, 
        header: "Score", 
        cell: { type: 'readonly' },
        width: 100
      }
    ];

    if (predictionResult?.movies?.[0]?.detailed_scores) {
      const detailedScoreKeys = Object.keys(predictionResult.movies[0].detailed_scores);
      const dynamicColumns: ColumnConfig[] = detailedScoreKeys.map(key => ({
        key,
        header: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        cell: { type: 'readonly' },
        width: 120,
        resizable: true,
        formatter: formatScore,
      }));
      return [...baseColumns, ...dynamicColumns];
    }

    return baseColumns;
  };

  useEffect(() => {
    if (!predictionResult?.movies) return;
    
    try {
      const newColumns = generateColumns();
      setColumns(newColumns);
      
      const detailedData = predictionResult.movies.map((movie: any) => {
        const detailedScore = movie.detailed_scores || {};
        
        const rowData: Record<string, any> = {
          [LOAD_TABLE_FIELDS.internal_id]: movie.internal_id || '',
          [LOAD_TABLE_FIELDS.version_id]: movie.version_id || '',
          [LOAD_TABLE_FIELDS.title]: movie.title || '',
          [LOAD_TABLE_FIELDS.release_date]: movie.release_date || '',
          [LOAD_TABLE_FIELDS.pred_roi]: movie.pred_roi || '',
          [LOAD_TABLE_FIELDS.box_office_multiple]: movie.box_office_multiple || '',
          [LOAD_TABLE_FIELDS.resulting_gross]: movie.resulting_gross || '',
          [LOAD_TABLE_FIELDS.score]: movie.score || ''
        };

        Object.entries(detailedScore).forEach(([key, value]) => {
          rowData[key] = String(value);
        });

        return rowData;
      });
      
      setTableData(detailedData);
    } catch (parseError) {
      console.error('Error processing prediction data:', parseError);
    }
  }, [predictionResult]);

  const selectedRow = (location.state as any)?.selectedRow;
  const [tableData, setTableData] = useState<TableRow[]>([
    selectedRow || {
      [LOAD_TABLE_FIELDS.internal_id]: "",
      [LOAD_TABLE_FIELDS.version_id]: "",
      [LOAD_TABLE_FIELDS.title]: "",
      [LOAD_TABLE_FIELDS.release_date]: "",
      [LOAD_TABLE_FIELDS.pred_roi]: "",
      [LOAD_TABLE_FIELDS.box_office_multiple]: "",
      [LOAD_TABLE_FIELDS.resulting_gross]: "",
      [LOAD_TABLE_FIELDS.score]: ""
    }
  ]);

  const handleCellChange = (rowIndex: number, column: string, value: string | any[]): void => {};

  const addNewRow = (): void => {};

  const handleExport = (format: ExportFormat): void => {};

  const handleBackToPredict = (): void => {
    history.push(`${routes.PREDICTIONS_ENGINE}/predict`);
  };

  const isExportAllowed = predictionResult?.movies?.some(movie => movie.is_export_allowed === true) || false;

  const actionButtons = [
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" disabled={!isExportAllowed}>
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => handleExport('CSV')}>
          Export as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>,

    <Button variant="default" onClick={handleBackToPredict}>
      Back to Predict
    </Button>
  ];

  if (!predictionResult?.movies || predictionResult.movies.length === 0) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        margin: '20px'
      }}>
        <h4>No Detailed Data Available</h4>
        <p>No prediction results found. Please run a prediction first.</p>
        <Button variant="default" onClick={handleBackToPredict}>
          Back to Predict
        </Button>
      </div>
    );
  }

  return (
    <PredictionsTable
      actionButtons={actionButtons}
      columns={columns}
      tableData={tableData}
      onCellChange={handleCellChange}
      onAddRow={addNewRow}
      disableAddButton={true}
      disableModelSelection={true}
    />
  );
};

export default DetailedRoute;